import { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BugReportIcon from '@mui/icons-material/BugReport';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useTranslation } from 'react-i18next';
import { useNews } from '../../contexts/NewsContext';
import NewsDialog from './NewsDialog';
import NewsAdminPanel from './NewsAdminPanel';

// Map icon names to components
const iconMap = {
  AutoAwesome: AutoAwesomeIcon,
  TrendingUp: TrendingUpIcon,
  BugReport: BugReportIcon,
  Campaign: CampaignIcon,
};

// Map color names to MUI colors
const colorMap = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  primary: 'primary',
  error: 'error',
};

function NewsIcon() {
  const { t } = useTranslation();
  const { 
    news, 
    unreadCount, 
    isAdmin, 
    loading, 
    fetchNews, 
    markAsRead,
    markAllAsRead 
  } = useNews();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh news when opening
    fetchNews();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewsClick = async (newsItem) => {
    setSelectedNews(newsItem);
    setDialogOpen(true);
    handleClose();
    
    // Mark as read when opened
    if (!newsItem.is_read) {
      await markAsRead(newsItem.id);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedNews(null);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleAdminClick = () => {
    handleClose();
    setAdminPanelOpen(true);
  };

  const handleAdminClose = () => {
    setAdminPanelOpen(false);
    // Refresh news after admin changes
    fetchNews();
  };

  // Get icon component for category
  const getCategoryIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || CampaignIcon;
    return <IconComponent fontSize="small" />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return t('news.today');
    } else if (diffDays === 1) {
      return t('news.yesterday');
    } else if (diffDays < 7) {
      return t('news.daysAgo', { days: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  // Recent news (max 5 items)
  const recentNews = news.slice(0, 5);

  return (
    <>
      <Tooltip title={t('news.title')}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: '#ADB5BD',
            mr: 1,
            '&:hover': { color: '#90caf9' }
          }}
        >
          <Badge 
            badgeContent={unreadCount > 9 ? '9+' : unreadCount} 
            color="error"
            invisible={unreadCount === 0}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon />
            ) : (
              <NotificationsNoneIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('news.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button 
              size="small" 
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none' }}
            >
              {t('news.markAllRead')}
            </Button>
          )}
        </Box>
        
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : recentNews.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('news.noNews')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {recentNews.map((item) => (
              <ListItem 
                key={item.id} 
                disablePadding
                sx={{
                  bgcolor: item.is_read ? 'transparent' : 'rgba(144, 202, 249, 0.08)',
                }}
              >
                <ListItemButton onClick={() => handleNewsClick(item)}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                    <Box sx={{ 
                      color: `${colorMap[item.category_color] || 'primary'}.main`,
                      mt: 0.5
                    }}>
                      {getCategoryIcon(item.category_icon)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: item.is_read ? 400 : 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                        >
                          {item.title}
                        </Typography>
                        {!item.is_read && (
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main',
                              flexShrink: 0
                            }} 
                          />
                        )}
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.summary || item.content?.substring(0, 100)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={item.category_name} 
                          size="small" 
                          color={colorMap[item.category_color] || 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(item.published_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        <Divider />

        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
          {news.length > 5 && (
            <Button 
              size="small" 
              onClick={() => {
                handleClose();
                setDialogOpen(true);
                setSelectedNews(null); // Will show all news
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('news.viewAll')}
            </Button>
          )}
          {isAdmin && (
            <Button 
              size="small" 
              color="secondary"
              onClick={handleAdminClick}
              sx={{ textTransform: 'none', ml: 'auto' }}
            >
              {t('news.admin.manage')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* News Detail Dialog */}
      <NewsDialog 
        open={dialogOpen}
        onClose={handleDialogClose}
        news={selectedNews}
        allNews={selectedNews === null ? news : null}
      />

      {/* Admin Panel */}
      {isAdmin && (
        <NewsAdminPanel 
          open={adminPanelOpen}
          onClose={handleAdminClose}
        />
      )}
    </>
  );
}

export default NewsIcon;
